// src/components/registration/steps/StepAddUserGroups.jsx
import React, { useState, useEffect } from "react";
import MultiStyleButton from "../../systemUtils/MultiStyleButton";
import StepBar from "../../../utils/StepBar";
import heroLogo from "../../../../images/heroLogo.png";
import UserGroup from "./supportComponents/UserGroup";
import validate from "./functions/validation";
import LoadingCircle from "../../../utils/LoadingCircle";
import OutputScreen from "../../systemUtils/OutputScreen";
import { finalizeUserStructure } from "./functions/finalizeUserStructure";
import "../RegisterSteps.css";
import { useDispatch, useSelector } from "react-redux";
import { setRegistrationStep } from "../../../../redux/actionsFolder/registrationActions";
import { useNavigate } from "react-router-dom";

const StepAddUserGroups = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const registrationData = useSelector(
    (state) => state.registration.registrationData
  );

  /* ───────────────────────────────────────────────
   *  Guard: reroute if earlier steps not complete
   * ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!registrationData.userEmail || !registrationData.company) {
      navigate("/register-hero/welcome");
    }
  }, [registrationData, navigate]);

  /* ────────────── Local state ────────────── */
  const [userGroups, setUserGroups]               = useState([]);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const [isProcessing, setIsProcessing]           = useState(false);
  const [operationResults, setOperationResults]   = useState(null);
  const [history, setHistory]                     = useState([]);
  const [future, setFuture]                       = useState([]);
  const [masterEmails, setMasterEmails]           = useState([]);

  /* ─────────────────────────────────────────
   *  Helper: ensure e-mail is unique globally
   * ───────────────────────────────────────── */
  const checkDuplicateEmails = (emails) => {
    for (const em of emails) {
      if (masterEmails.includes(em)) return false;
    }
    setMasterEmails((prev) => [...prev, ...emails]);
    return true;
  };

  /* ────────────── CRUD helpers ───────────── */
  const addUserGroup = () => {
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    setUserGroups([
      ...userGroups,
      { groupName: "", hierarchy: userGroups.length + 1, roles: [] },
    ]);
    setFuture([]);
  };

  const removeUserGroup = (index) => {
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    const updated = userGroups.filter((_, i) => i !== index);
    updated.forEach((g, idx) => (g.hierarchy = idx + 1));
    setUserGroups(updated);
    setFuture([]);
  };

  const updateUserGroup = (index, field, value) => {
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    const updated = [...userGroups];
    updated[index][field] = value;
    setUserGroups(updated);
    setFuture([]);
  };

  const addRole = (groupIndex) => {
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    const updated = [...userGroups];
    updated[groupIndex].roles.push({ roleName: "", users: [] });
    setUserGroups(updated);
    setFuture([]);
  };

  const removeRole = (groupIndex, roleIndex) => {
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    const updated = [...userGroups];
    updated[groupIndex].roles.splice(roleIndex, 1);
    setUserGroups(updated);
    setFuture([]);
  };

  const updateRole = (groupIndex, roleIndex, field, value) => {
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    const updated = [...userGroups];
    updated[groupIndex].roles[roleIndex][field] = value;
    setUserGroups(updated);
    setFuture([]);
  };

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture([userGroups, ...future]);
    setUserGroups(prev);
    setHistory(history.slice(0, -1));
  };

  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setHistory([...history, JSON.parse(JSON.stringify(userGroups))]);
    setUserGroups(next);
    setFuture(future.slice(1));
  };

  /* ────────────── Flow control ───────────── */
  const handleContinue = () => {
    const { isValid } = validate(userGroups);
    if (!isValid) {
      alert("Please fix the errors in the form before proceeding.");
      return;
    }
    setShowFinalConfirmation(true);
  };

  const handleFinalConfirm = async () => {
    try {
      setIsProcessing(true);
      const results = await finalizeUserStructure({
        userGroups,
        company:          registrationData.company,
        collectionBrand:  registrationData.collectionBrand,
        systemNameConfig: "companyStructure",
        userEmail:        registrationData.userEmail,
      });
      setOperationResults(results);
    } catch (err) {
      console.error("Error in handleFinalConfirm:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinueEditing = () => setShowFinalConfirmation(false);

  /* ────────────── JSX helpers ───────────── */
  const renderUserCreatedStructure = () => (
    <div className="slider-button-box">
      <div className="predefined-structure-visual">
        <h3 className="predefined-structure-title">
          Your New User Group Hierarchy
        </h3>
        <ul className="group-list">
          {userGroups.map((g, gi) => (
            <li key={gi} className="group-item">
              <strong>{g.groupName || "Unnamed Group"} (Hierarchy = {g.hierarchy})</strong>
              <ul className="role-list">
                {g.roles.map((r, ri) => (
                  <li key={ri} className="role-item">
                    Role: <em>{r.roleName || "Unnamed Role"}</em>
                    <ul className="user-list">
                      {r.users.length
                        ? r.users.map((u, ui) => <li key={ui}>{u}</li>)
                        : <li className="user-item no-users">No users added.</li>}
                    </ul>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
      <div className="continue-button-container">
        <MultiStyleButton label="Confirm"          buttonType="approve" onClick={handleFinalConfirm} />
        <MultiStyleButton label="Continue Editing" buttonType="cancel"  onClick={handleContinueEditing} />
      </div>
    </div>
  );

  /* ────────────── Render ───────────── */
  return (
    <div className="step-add-user-groups-container">
      <div className="top-bar">
        <div className="logo-container">
          <img src={heroLogo} alt="Hero Logo" className="logo-small" />
        </div>
        <StepBar totalSteps={4} currentStep={3} />
      </div>

      <h2 className="step-title centered">Add User Groups</h2>
      <p className="step-description centered">
        Set up your organization's structure by creating User Groups…
      </p>

      {isProcessing && (
        <div className="loading-overlay">
          <LoadingCircle label="Processing your request…" />
        </div>
      )}

      {operationResults && !isProcessing && (
        <OutputScreen
          title="User Group Setup & Invitations"
          info="Below are the results of your user group configuration and invitations."
          dataObjects={[
            {
              title: "Group Document",
              data: {
                "Firestore Doc ID": operationResults.structureDocId,
                "Total Invites":     operationResults.totalInvites,
              },
            },
            {
              title: "Invite Details",
              data: operationResults.inviteDetails.reduce((acc, itm, idx) => {
                acc[`Invite #${idx + 1}`] = itm;
                return acc;
              }, {}),
            },
          ]}
          onContinue={() => {
            dispatch(setRegistrationStep("system-permissions"));
            navigate("/register-hero/system-permissions");
          }}
        />
      )}

      {showFinalConfirmation && !operationResults && !isProcessing && renderUserCreatedStructure()}

      {!showFinalConfirmation && !operationResults && !isProcessing && (
        <>
          {/* Existing example hierarchy (unchanged) */}
          <div className="slider-button-box">
            <div className="predefined-structure-visual">
              <h3 className="predefined-structure-title">Example User Group Hierarchy</h3>
              {/* … static example list … */}
            </div>
          </div>

          <div className="user-groups-container">
            {userGroups.map((group, groupIndex) => (
              <UserGroup
                key={groupIndex}
                groupIndex={groupIndex}
                group={group}
                updateUserGroup={updateUserGroup}
                removeUserGroup={removeUserGroup}
                addRole={addRole}
                removeRole={removeRole}
                updateRole={updateRole}
                checkDuplicateEmails={checkDuplicateEmails}   
              />
            ))}

            <div className="slider-button-box">
              <div className="add-user-group-container">
                <MultiStyleButton
                  label="Add User Group"
                  buttonType="wide-approve"
                  onClick={addUserGroup}
                />
              </div>
            </div>
          </div>

          <div className="continue-button-container">
            <MultiStyleButton
              label="Continue"
              buttonType="approve"
              onClick={handleContinue}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default StepAddUserGroups;
